/**
 * HomepageEditor — admin CMS for HomeLuxe sections. Grouped forms for:
 *   Hero · Trust Bar · Brand Story · Signature · Trade · Newsletter
 * Persists via PUT /api/admin/home. Empty fields fall back to defaults.
 */
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Save, RefreshCw, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { HeroImagePicker } from "./HeroImagePicker";

const inputCls =
  "w-full bg-transparent border border-hairline rounded-lg px-3 py-2.5 text-sm text-forest focus:outline-none focus:border-forest transition-colors";
const taCls = `${inputCls} min-h-[80px] resize-y`;

const Field = ({ label, children }) => (
  <label className="block">
    <div className="overline text-forest/60 mb-1.5">{label}</div>
    {children}
  </label>
);

const SectionCard = ({ title, kicker, children, onReset, testid }) => (
  <motion.section
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="border border-hairline rounded-lg p-6 md:p-8 bg-white"
    data-testid={testid}
  >
    <header className="flex items-start justify-between mb-5">
      <div>
        <div className="overline text-champagne">§ {kicker}</div>
        <h3 className="font-display text-forest text-2xl leading-none mt-1">{title}</h3>
      </div>
      {onReset && (
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 overline text-forest/60 hover:text-forest text-xs border border-hairline rounded-full px-3 py-1.5"
          data-testid={`${testid}-reset`}
        >
          <RotateCcw size={11} strokeWidth={1.6} /> Reset
        </button>
      )}
    </header>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </motion.section>
);

export const HomepageEditor = ({ authAxios }) => {
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const api = useMemo(() => authAxios(), [authAxios]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/home");
      setSections(data?.sections || {});
    } catch (e) {
      toast.error("Could not load homepage content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const patch = (key, field, val) => {
    setSections((s) => ({ ...s, [key]: { ...(s[key] || {}), [field]: val } }));
  };
  const patchNestedArr = (key, field, i, val) => {
    setSections((s) => {
      const cur = { ...(s[key] || {}) };
      const arr = Array.isArray(cur[field]) ? [...cur[field]] : [];
      arr[i] = val;
      cur[field] = arr;
      return { ...s, [key]: cur };
    });
  };
  const patchNestedObj = (key, field, i, subfield, val) => {
    setSections((s) => {
      const cur = { ...(s[key] || {}) };
      const arr = Array.isArray(cur[field]) ? [...cur[field]] : [];
      arr[i] = { ...(arr[i] || {}), [subfield]: val };
      cur[field] = arr;
      return { ...s, [key]: cur };
    });
  };
  const resetSection = async (key) => {
    if (!window.confirm(`Reset the ${key.replace("_", " ")} section to defaults?`)) return;
    try {
      await api.delete(`/admin/home/${key}`);
      setSections((s) => { const c = { ...s }; delete c[key]; return c; });
      toast.success("Section reset");
    } catch { toast.error("Could not reset section"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/home", { sections });
      toast.success("Homepage saved · refresh the homepage to see changes");
    } catch (e) {
      toast.error("Could not save homepage");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="py-24 flex items-center justify-center text-forest/60">
        <Loader2 size={16} strokeWidth={1.6} className="animate-spin mr-2" /> Loading homepage…
      </div>
    );
  }

  const hero = sections.hero || {};
  const trust = sections.trust_bar || {};
  const trustItems = Array.isArray(trust.items) ? trust.items : ["", "", "", "", ""];
  const brand = sections.brand_story || {};
  const signature = sections.signature || {};
  const trade = sections.trade || {};
  const audiences = Array.isArray(trade.audiences) ? trade.audiences : [{}, {}, {}];
  const news = sections.newsletter || {};
  const catsShow = sections.categories_showcase || {};
  const catsList = Array.isArray(catsShow.items) ? catsShow.items : [];
  const about = sections.about_story || {};
  const payoneer = sections.payoneer || {};
  const leadership = sections.leadership || {};
  // Sane defaults so a partial edit (e.g. name only) doesn't wipe out photo/role/bio.
  const LEADER_DEFAULTS = [
    { name: "M.D.S.R.", role: "CEO & Founder",  body: "Two decades of textile heritage. Leading Anabiya Decor's mission to serve hospitality and design-led buyers worldwide.", image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80" },
    { name: "M.D.S.R.", role: "Head Designer",  body: "Curating collections that balance timeless silhouettes with modern craftsmanship — every Anabiya piece begins on her desk.", image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=600&q=80" },
  ];
  const savedLeaders = Array.isArray(leadership.people) ? leadership.people : [];
  const leaders = LEADER_DEFAULTS.map((d, i) => ({ ...d, ...(savedLeaders[i] || {}) }));
  const setLeader = (i, key, val) => {
    const next = leaders.map((p) => ({ ...p })); // clone with defaults filled
    next[i] = { ...next[i], [key]: val };
    patch("leadership", "people", next);
  };

  return (
    <div className="space-y-6" data-testid="homepage-editor">
      {/* Top bar */}
      <div className="flex items-center justify-between sticky top-0 z-10 bg-white/80 backdrop-blur -mx-6 md:-mx-12 lg:-mx-16 px-6 md:px-12 lg:px-16 py-4 border-b border-hairline">
        <div>
          <div className="overline text-champagne">§ Homepage editor</div>
          <p className="text-forest/60 text-sm mt-0.5">Edits go live on <span className="tabnum">anabiyadecor.com</span> after you save.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 overline text-forest/70 hover:text-forest border border-hairline rounded-full px-4 py-2"
            data-testid="home-reload"
          >
            <RefreshCw size={12} strokeWidth={1.6} /> Reload
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 overline text-white bg-forest rounded-full px-5 py-2.5 disabled:opacity-40"
            data-testid="home-save"
          >
            {saving ? <Loader2 size={13} strokeWidth={1.8} className="animate-spin" /> : <Save size={13} strokeWidth={1.8} />}
            {saving ? "Saving…" : "Save homepage"}
          </button>
        </div>
      </div>

      {/* Hero */}
      <SectionCard title="Hero" kicker="Above the fold" testid="editor-hero" onReset={() => resetSection("hero")}>
        <div className="md:col-span-2">
          <div className="overline text-forest/60 mb-1.5">Hero layout</div>
          <div className="grid grid-cols-2 gap-3" data-testid="hero-layout-toggle">
            {[
              { id: "split", label: "Split (text left · image right)", hint: "Classic editorial layout" },
              { id: "full",  label: "Full (image full-width · text overlay)", hint: "Cinematic single photo hero" },
            ].map((opt) => {
              const active = (hero.layout || "split") === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => patch("hero", "layout", opt.id)}
                  data-testid={`hero-layout-${opt.id}`}
                  className={`text-left p-4 rounded-lg border transition-all ${active ? "bg-forest text-white border-forest" : "border-hairline text-forest hover:border-forest"}`}
                >
                  <div className="overline text-[10px] tracking-[3px] mb-1">{opt.label}</div>
                  <div className={`text-xs ${active ? "text-white/60" : "text-forest/50"}`}>{opt.hint}</div>
                </button>
              );
            })}
          </div>
        </div>

        {(hero.layout || "split") === "full" && (
          <>
            <Field label="Text alignment (full layout only)">
              <select
                className={inputCls}
                value={hero.text_align || "center"}
                onChange={(e) => patch("hero", "text_align", e.target.value)}
                data-testid="hero-text-align"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </Field>
            <Field label="Image overlay dimming (0 = crystal clear · 1 = solid)">
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="0"
                  max="0.9"
                  step="0.05"
                  value={hero.overlay ?? 0.5}
                  onChange={(e) => patch("hero", "overlay", Number(e.target.value))}
                  className="flex-1 accent-forest"
                  data-testid="hero-overlay-slider"
                />
                <span className="tabnum text-forest/70 text-xs w-12 text-right">{Number(hero.overlay ?? 0.5).toFixed(2)}</span>
              </div>
            </Field>
          </>
        )}

        <Field label="Kicker (small caps)">
          <input className={inputCls} value={hero.kicker || ""} onChange={(e) => patch("hero", "kicker", e.target.value)} placeholder="§ Est. Mumbai" data-testid="hero-kicker" />
        </Field>
        <Field label="Announcement pill">
          <input className={inputCls} value={hero.announcement || ""} onChange={(e) => patch("hero", "announcement", e.target.value)} placeholder="Now shipping to 30+ countries" data-testid="hero-announcement" />
        </Field>
        <Field label="Background colour (hex — leave empty for default forest green)">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={hero.bg_color || "#132923"}
              onChange={(e) => patch("hero", "bg_color", e.target.value)}
              className="h-11 w-14 rounded-lg border border-hairline cursor-pointer"
              data-testid="hero-bg-color-picker"
            />
            <input
              className={inputCls}
              value={hero.bg_color || ""}
              onChange={(e) => patch("hero", "bg_color", e.target.value)}
              placeholder="#132923 (or #ffffff, #1a1a2e, #f7f1e8…)"
              data-testid="hero-bg-color-hex"
            />
            {hero.bg_color && (
              <button
                type="button"
                onClick={() => patch("hero", "bg_color", "")}
                className="overline text-xs text-forest/60 hover:text-forest border border-hairline rounded-full px-3 py-2"
                data-testid="hero-bg-color-reset"
              >
                Reset
              </button>
            )}
          </div>
        </Field>
        <Field label="Preset colours">
          <div className="flex flex-wrap gap-2" data-testid="hero-bg-presets">
            {[
              { label: "Forest (default)", hex: "" },
              { label: "Ivory Cream", hex: "#F7F1E8" },
              { label: "Warm White", hex: "#FBF9F4" },
              { label: "Midnight", hex: "#0F1826" },
              { label: "Deep Wine", hex: "#3B1120" },
              { label: "Charcoal", hex: "#1F1F1F" },
              { label: "Sand", hex: "#D9C7A7" },
            ].map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => patch("hero", "bg_color", p.hex)}
                title={p.label}
                data-testid={`hero-bg-preset-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="h-8 w-8 rounded-full border border-hairline hover:ring-2 hover:ring-forest transition-shadow"
                style={{ background: p.hex || "hsl(150 60% 18%)" }}
              />
            ))}
          </div>
        </Field>
        <div className="md:col-span-2">
          <Field label="Title (main line)">
            <input className={inputCls} value={hero.title || ""} onChange={(e) => patch("hero", "title", e.target.value)} placeholder="The house of" data-testid="hero-title" />
          </Field>
        </div>
        <Field label="Title (italic accent)">
          <input className={inputCls} value={hero.title_accent || ""} onChange={(e) => patch("hero", "title_accent", e.target.value)} placeholder="quiet luxury." data-testid="hero-title-accent" />
        </Field>
        <div className="md:col-span-2">
          <HeroImagePicker
            label="Background image (fallback for single-slide hero)"
            value={hero.image || ""}
            onChange={(url) => patch("hero", "image", url)}
            api={api}
            testidPrefix="hero-image"
          />
        </div>
        <div className="md:col-span-2">
          <Field label="Subtitle / lede">
            <textarea className={taCls} value={hero.subtitle || ""} onChange={(e) => patch("hero", "subtitle", e.target.value)} placeholder="Wholesale bedding and home textiles crafted in Mumbai…" data-testid="hero-subtitle" />
          </Field>
        </div>
        <Field label="Primary CTA text">
          <input className={inputCls} value={hero.cta_primary || ""} onChange={(e) => patch("hero", "cta_primary", e.target.value)} placeholder="Request a wholesale quote" data-testid="hero-cta-primary" />
        </Field>
        <Field label="Secondary CTA text">
          <input className={inputCls} value={hero.cta_secondary || ""} onChange={(e) => patch("hero", "cta_secondary", e.target.value)} placeholder="Browse the catalogue" data-testid="hero-cta-secondary" />
        </Field>

        {/* Slider builder */}
        <div className="md:col-span-2 border-t border-hairline pt-5 mt-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline text-champagne">§ Hero slider</div>
              <p className="text-forest/60 text-xs mt-0.5">Add up to 5 slides. Leave empty to show the single hero above. Each slide auto-plays every ~6s.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                const slides = Array.isArray(hero.slides) ? hero.slides : [];
                if (slides.length >= 5) return toast.error("Max 5 slides");
                patch("hero", "slides", [...slides, {
                  image: "",
                  kicker: "",
                  title: "",
                  title_accent: "",
                  subtitle: "",
                  cta_primary: "Shop Now",
                  cta_primary_link: "/shop",
                  cta_secondary: "",
                  cta_secondary_link: "",
                }]);
              }}
              className="inline-flex items-center gap-1 overline text-forest border border-hairline rounded-full px-4 py-2 text-xs hover:border-forest"
              data-testid="hero-slide-add"
            >
              + Add slide
            </button>
          </div>

          {(hero.slides || []).length === 0 && (
            <div className="text-forest/40 text-xs italic border border-dashed border-hairline rounded-lg p-6 text-center">
              No slider slides yet. Click "Add slide" to build a rotating hero. When at least 1 slide exists, it replaces the static hero above.
            </div>
          )}

          <div className="space-y-4">
            {(hero.slides || []).map((slide, i) => (
              <div key={i} className="border border-hairline rounded-lg p-4 bg-cream/40" data-testid={`hero-slide-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="overline text-forest/60">Slide {i + 1}</div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const slides = [...(hero.slides || [])];
                        if (i === 0) return;
                        [slides[i - 1], slides[i]] = [slides[i], slides[i - 1]];
                        patch("hero", "slides", slides);
                      }}
                      disabled={i === 0}
                      className="overline text-xs text-forest/70 hover:text-forest disabled:opacity-30 border border-hairline rounded-full px-2.5 py-1"
                      data-testid={`hero-slide-up-${i}`}
                    >↑</button>
                    <button
                      type="button"
                      onClick={() => {
                        const slides = [...(hero.slides || [])];
                        if (i >= slides.length - 1) return;
                        [slides[i], slides[i + 1]] = [slides[i + 1], slides[i]];
                        patch("hero", "slides", slides);
                      }}
                      disabled={i >= (hero.slides || []).length - 1}
                      className="overline text-xs text-forest/70 hover:text-forest disabled:opacity-30 border border-hairline rounded-full px-2.5 py-1"
                      data-testid={`hero-slide-down-${i}`}
                    >↓</button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!window.confirm(`Delete slide ${i + 1}?`)) return;
                        patch("hero", "slides", (hero.slides || []).filter((_, idx) => idx !== i));
                      }}
                      className="overline text-xs text-red-600 hover:text-red-800 border border-red-200 rounded-full px-2.5 py-1"
                      data-testid={`hero-slide-remove-${i}`}
                    >Remove</button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <HeroImagePicker
                      label={`Slide ${i + 1} image`}
                      value={slide.image || ""}
                      onChange={(url) => patchNestedObj("hero", "slides", i, "image", url)}
                      api={api}
                      testidPrefix={`hero-slide-img-${i}`}
                    />
                  </div>
                  <Field label="Kicker (small caps)">
                    <input className={inputCls} value={slide.kicker || ""} onChange={(e) => patchNestedObj("hero", "slides", i, "kicker", e.target.value)} placeholder="§ New Collection" data-testid={`hero-slide-kicker-${i}`} />
                  </Field>
                  <Field label="Title">
                    <input className={inputCls} value={slide.title || ""} onChange={(e) => patchNestedObj("hero", "slides", i, "title", e.target.value)} placeholder="Luxury Bedding," data-testid={`hero-slide-title-${i}`} />
                  </Field>
                  <Field label="Title (italic accent)">
                    <input className={inputCls} value={slide.title_accent || ""} onChange={(e) => patchNestedObj("hero", "slides", i, "title_accent", e.target.value)} placeholder="Crafted for Beautiful Homes." data-testid={`hero-slide-accent-${i}`} />
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Subtitle">
                      <textarea className={taCls} value={slide.subtitle || ""} onChange={(e) => patchNestedObj("hero", "slides", i, "subtitle", e.target.value)} placeholder="Premium duvet covers, sheet sets, quilts…" data-testid={`hero-slide-subtitle-${i}`} />
                    </Field>
                  </div>
                  <Field label="Primary CTA text">
                    <input className={inputCls} value={slide.cta_primary || ""} onChange={(e) => patchNestedObj("hero", "slides", i, "cta_primary", e.target.value)} placeholder="Shop Bedding" data-testid={`hero-slide-cta1-${i}`} />
                  </Field>
                  <Field label='Primary CTA link (e.g. "/shop?category=duvet-covers")'>
                    <input className={inputCls} value={slide.cta_primary_link || ""} onChange={(e) => patchNestedObj("hero", "slides", i, "cta_primary_link", e.target.value)} placeholder="/shop" data-testid={`hero-slide-cta1link-${i}`} />
                  </Field>
                  <Field label="Secondary CTA text (optional)">
                    <input className={inputCls} value={slide.cta_secondary || ""} onChange={(e) => patchNestedObj("hero", "slides", i, "cta_secondary", e.target.value)} placeholder="Explore Bestsellers" data-testid={`hero-slide-cta2-${i}`} />
                  </Field>
                  <Field label='Secondary CTA link (optional, e.g. "#bestsellers")'>
                    <input className={inputCls} value={slide.cta_secondary_link || ""} onChange={(e) => patchNestedObj("hero", "slides", i, "cta_secondary_link", e.target.value)} placeholder="#bestsellers" data-testid={`hero-slide-cta2link-${i}`} />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* Payoneer / Bank Transfer for checkout */}
      <SectionCard title="Payoneer / Bank Transfer" kicker="Checkout · International" testid="editor-payoneer" onReset={() => resetSection("payoneer")}>
        <div className="md:col-span-2">
          <label className="inline-flex items-center gap-2 text-forest text-sm">
            <input
              type="checkbox"
              checked={!!payoneer.enabled}
              onChange={(e) => patch("payoneer", "enabled", e.target.checked)}
              className="accent-forest"
              data-testid="payoneer-enabled"
            />
            Show bank-transfer option at checkout
          </label>
          <p className="text-forest/50 text-xs mt-1">
            Turn this on once you paste your Payoneer receiving-account details below. Buyers will see the option beside Razorpay / PayPal / WhatsApp on the checkout page.
          </p>
        </div>
        <Field label='Block title (e.g. "Pay by Bank Transfer (Payoneer)")'>
          <input className={inputCls} value={payoneer.title || ""} onChange={(e) => patch("payoneer", "title", e.target.value)} placeholder="Pay by Bank Transfer (Payoneer)" data-testid="payoneer-title" />
        </Field>
        <Field label="Reference / order note">
          <input className={inputCls} value={payoneer.reference || ""} onChange={(e) => patch("payoneer", "reference", e.target.value)} placeholder="Use your name + order number as reference" data-testid="payoneer-reference" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Subtitle shown to buyer">
            <textarea className={taCls} value={payoneer.subtitle || ""} onChange={(e) => patch("payoneer", "subtitle", e.target.value)} placeholder="For USA / UK / EU / AU buyers. Wire to our Payoneer receiving account, then WhatsApp us the receipt." data-testid="payoneer-subtitle" />
          </Field>
        </div>
        <Field label="Beneficiary name">
          <input className={inputCls} value={payoneer.beneficiary || ""} onChange={(e) => patch("payoneer", "beneficiary", e.target.value)} placeholder="Anabiya Decor" data-testid="payoneer-beneficiary" />
        </Field>
        <Field label="Bank name">
          <input className={inputCls} value={payoneer.bank_name || ""} onChange={(e) => patch("payoneer", "bank_name", e.target.value)} placeholder="Community Federal Savings Bank (Payoneer)" data-testid="payoneer-bank" />
        </Field>
        <Field label="Account number">
          <input className={inputCls} value={payoneer.account_number || ""} onChange={(e) => patch("payoneer", "account_number", e.target.value)} placeholder="1234567890" data-testid="payoneer-account" />
        </Field>
        <Field label="Routing number (US ACH)">
          <input className={inputCls} value={payoneer.routing_number || ""} onChange={(e) => patch("payoneer", "routing_number", e.target.value)} placeholder="026073150" data-testid="payoneer-routing" />
        </Field>
        <Field label="SWIFT / BIC">
          <input className={inputCls} value={payoneer.swift || ""} onChange={(e) => patch("payoneer", "swift", e.target.value)} placeholder="CMFGUS33" data-testid="payoneer-swift" />
        </Field>
        <Field label="IBAN (EU)">
          <input className={inputCls} value={payoneer.iban || ""} onChange={(e) => patch("payoneer", "iban", e.target.value)} placeholder="GB29 NWBK 6016 1331 9268 19" data-testid="payoneer-iban" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Branch / bank address">
            <textarea className={taCls} value={payoneer.branch_address || ""} onChange={(e) => patch("payoneer", "branch_address", e.target.value)} placeholder="810 Seventh Ave, New York, NY 10019, USA" data-testid="payoneer-branch" />
          </Field>
        </div>
      </SectionCard>

      {/* Leadership — CEO + Head Designer */}
      <SectionCard title="Leadership · CEO + Designer" kicker="Founder cards" testid="editor-leadership" onReset={() => resetSection("leadership")}>
        <Field label="Kicker">
          <input className={inputCls} value={leadership.kicker || ""} onChange={(e) => patch("leadership", "kicker", e.target.value)} placeholder="LEADERSHIP" data-testid="leader-kicker" />
        </Field>
        <Field label="Headline">
          <input className={inputCls} value={leadership.headline || ""} onChange={(e) => patch("leadership", "headline", e.target.value)} placeholder="Guided by Vision," data-testid="leader-headline" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Headline (italic accent)">
            <input className={inputCls} value={leadership.headline_accent || ""} onChange={(e) => patch("leadership", "headline_accent", e.target.value)} placeholder=" Driven by Passion." data-testid="leader-accent" />
          </Field>
        </div>

        {[0, 1].map((i) => {
          const p = leaders[i] || {};
          const testidPrefix = i === 0 ? "leader-ceo" : "leader-designer";
          const title = i === 0 ? "Founder / CEO" : "Head Designer";
          return (
            <div key={i} className="md:col-span-2 border-t border-hairline pt-6 mt-2" data-testid={`leader-card-${i}`}>
              <div className="overline text-champagne mb-3">§ {title}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <HeroImagePicker
                    label={`${title} — headshot`}
                    value={p.image || ""}
                    onChange={(url) => setLeader(i, "image", url)}
                    api={api}
                    portrait={true}
                    aiKind="portrait"
                    testidPrefix={`${testidPrefix}-img`}
                  />
                </div>
                <Field label="Full name">
                  <input className={inputCls} value={p.name || ""} onChange={(e) => setLeader(i, "name", e.target.value)} placeholder="Ms. Anabiya Rehman" data-testid={`${testidPrefix}-name`} />
                </Field>
                <Field label="Role / title">
                  <input className={inputCls} value={p.role || ""} onChange={(e) => setLeader(i, "role", e.target.value)} placeholder={title} data-testid={`${testidPrefix}-role`} />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Short bio (2-3 lines)">
                    <textarea className={taCls} value={p.body || ""} onChange={(e) => setLeader(i, "body", e.target.value)} placeholder="Two decades of textile heritage — leading Anabiya Decor's mission to serve hospitality and design-led buyers worldwide." data-testid={`${testidPrefix}-bio`} />
                  </Field>
                </div>
              </div>
            </div>
          );
        })}
      </SectionCard>

      {/* Trust bar */}
      <SectionCard title="Trust bar" kicker="Marquee strip" testid="editor-trust" onReset={() => resetSection("trust_bar")}>
        {trustItems.map((t, i) => (
          <Field key={i} label={`Item ${i + 1}`}>
            <input className={inputCls} value={t || ""} onChange={(e) => patchNestedArr("trust_bar", "items", i, e.target.value)} placeholder={`e.g. Serving hotels across 30+ countries`} data-testid={`trust-item-${i}`} />
          </Field>
        ))}
      </SectionCard>

      {/* Brand story */}
      <SectionCard title="Brand story" kicker="The house" testid="editor-brand" onReset={() => resetSection("brand_story")}>
        <Field label="Kicker">
          <input className={inputCls} value={brand.kicker || ""} onChange={(e) => patch("brand_story", "kicker", e.target.value)} placeholder="§ The house of Anabiya" data-testid="brand-kicker" />
        </Field>
        <Field label="Headline">
          <input className={inputCls} value={brand.headline || ""} onChange={(e) => patch("brand_story", "headline", e.target.value)} placeholder="Crafted in Mumbai. Shipped worldwide." data-testid="brand-headline" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Body">
            <textarea className={taCls} value={brand.body || ""} onChange={(e) => patch("brand_story", "body", e.target.value)} data-testid="brand-body" />
          </Field>
        </div>
      </SectionCard>

      {/* Signature block */}
      <SectionCard title="Signature block" kicker="Editorial slot" testid="editor-signature" onReset={() => resetSection("signature")}>
        <Field label="Kicker">
          <input className={inputCls} value={signature.kicker || ""} onChange={(e) => patch("signature", "kicker", e.target.value)} placeholder="§ Featured piece" data-testid="signature-kicker" />
        </Field>
        <Field label="Headline">
          <input className={inputCls} value={signature.headline || ""} onChange={(e) => patch("signature", "headline", e.target.value)} placeholder="The Ivoire Sateen story" data-testid="signature-headline" />
        </Field>
        <Field label="Image URL">
          <input className={inputCls} value={signature.image || ""} onChange={(e) => patch("signature", "image", e.target.value)} placeholder="https://…" data-testid="signature-image" />
        </Field>
        <Field label="CTA text">
          <input className={inputCls} value={signature.cta || ""} onChange={(e) => patch("signature", "cta", e.target.value)} placeholder="Explore duvet covers" data-testid="signature-cta" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Body">
            <textarea className={taCls} value={signature.body || ""} onChange={(e) => patch("signature", "body", e.target.value)} data-testid="signature-body" />
          </Field>
        </div>
      </SectionCard>

      {/* Trade audiences */}
      <SectionCard title="Trade section" kicker="Audience cards" testid="editor-trade" onReset={() => resetSection("trade")}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="md:col-span-1 border border-hairline rounded-lg p-4 space-y-2 bg-cream">
            <div className="overline text-champagne text-xs">Audience {i + 1}</div>
            <Field label="Title">
              <input className={inputCls} value={audiences[i]?.title || ""} onChange={(e) => patchNestedObj("trade", "audiences", i, "title", e.target.value)} placeholder="Hotels & Resorts" data-testid={`trade-title-${i}`} />
            </Field>
            <Field label="Body">
              <textarea className={taCls} value={audiences[i]?.body || ""} onChange={(e) => patchNestedObj("trade", "audiences", i, "body", e.target.value)} data-testid={`trade-body-${i}`} />
            </Field>
          </div>
        ))}
      </SectionCard>

      {/* Categories showcase */}
      <SectionCard title="Category tiles (homepage)" kicker="Photo per tile — replace / upload / AI" testid="editor-categories-showcase" onReset={() => resetSection("categories_showcase")}>
        <Field label="Section kicker">
          <input className={inputCls} value={catsShow.kicker || ""} onChange={(e) => patch("categories_showcase", "kicker", e.target.value)} placeholder="SHOP BY CATEGORY" data-testid="catshow-kicker" />
        </Field>
        <Field label="Section headline">
          <input className={inputCls} value={catsShow.headline || ""} onChange={(e) => patch("categories_showcase", "headline", e.target.value)} placeholder="Explore Our Collections" data-testid="catshow-headline" />
        </Field>
        <div className="md:col-span-2">
          {(() => {
            // Default 12 category tiles the homepage falls back to.
            const DEFAULT_TILES = [
              { label: "Duvet Covers",     slug: "duvet-covers" },
              { label: "Sheet Sets",       slug: "sheet-sets" },
              { label: "Curtains",         slug: "curtains" },
              { label: "Bed Skirts",       slug: "bed-skirts" },
              { label: "Headboards",       slug: "headboards" },
              { label: "Bath Towels",      slug: "bath-towels" },
              { label: "Bathrobes",        slug: "bathrobes" },
              { label: "Cushion Covers",   slug: "cushion-covers" },
              { label: "Winter Jackets",   slug: "winter-jackets" },
              { label: "Ladies Dresses",   slug: "linen-dresses" },
              { label: "Handmade Items",   slug: "handmade-gifts" },
              { label: "Windows & Blinds", slug: "windows" },
            ];
            const savedTiles = Array.isArray(catsShow.items) ? catsShow.items.filter((t) => t && t.slug) : [];
            const tiles = DEFAULT_TILES.map((d) => {
              const saved = savedTiles.find((s) => s.slug === d.slug);
              return { ...d, ...(saved || {}) };
            });
            const extra = savedTiles.filter((s) => !DEFAULT_TILES.find((d) => d.slug === s.slug));
            const all = [...tiles, ...extra];

            const setTile = (slug, key, value) => {
              // Replace the whole items array — merge in the given slug's change.
              const map = Object.fromEntries(all.map((t) => [t.slug, { ...t }]));
              map[slug] = { ...(map[slug] || { slug }), [key]: value };
              const next = Object.values(map).map((t) => ({ label: t.label || "", slug: t.slug, image: t.image || "" }));
              patch("categories_showcase", "items", next);
            };

            return (
              <div className="space-y-4" data-testid="catshow-tiles">
                <p className="text-forest/50 text-xs italic">
                  Upload or AI-generate a photo for each of your homepage category tiles. The photo shown here will replace the stock image on the public homepage. Leave a tile empty to fall back to the built-in stock photo.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {all.map((tile, i) => (
                    <div key={tile.slug} className="border border-hairline rounded-lg p-4 bg-cream/40" data-testid={`catshow-tile-${tile.slug}`}>
                      <div className="flex items-center justify-between mb-2">
                        <input
                          className={`${inputCls} font-display text-base tracking-wide`}
                          value={tile.label || ""}
                          onChange={(e) => setTile(tile.slug, "label", e.target.value)}
                          placeholder={tile.slug}
                          data-testid={`catshow-label-${tile.slug}`}
                        />
                      </div>
                      <div className="overline text-forest/40 text-[10px] mb-2">/shop?category={tile.slug}</div>
                      <HeroImagePicker
                        label=""
                        value={tile.image || ""}
                        onChange={(url) => setTile(tile.slug, "image", url)}
                        api={api}
                        testidPrefix={`catshow-img-${tile.slug}`}
                        aspect="4/5"
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </SectionCard>

      {/* About / Our Story page */}
      <SectionCard title='"Our Story" page' kicker="About content" testid="editor-about" onReset={() => resetSection("about_story")}>
        <Field label="Kicker">
          <input className={inputCls} value={about.kicker || ""} onChange={(e) => patch("about_story", "kicker", e.target.value)} placeholder="§ Our Story" data-testid="about-kicker" />
        </Field>
        <Field label="Headline">
          <input className={inputCls} value={about.headline || ""} onChange={(e) => patch("about_story", "headline", e.target.value)} placeholder="A Legacy of Craftsmanship" data-testid="about-headline" />
        </Field>
        <Field label="Headline accent (italic)">
          <input className={inputCls} value={about.headline_accent || ""} onChange={(e) => patch("about_story", "headline_accent", e.target.value)} placeholder=" and Quality." data-testid="about-headline-accent" />
        </Field>
        <Field label="Hero image URL">
          <input className={inputCls} value={about.image || ""} onChange={(e) => patch("about_story", "image", e.target.value)} placeholder="https://…" data-testid="about-image" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Story paragraph 1">
            <textarea className={taCls} value={about.p1 || ""} onChange={(e) => patch("about_story", "p1", e.target.value)} data-testid="about-p1" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Story paragraph 2">
            <textarea className={taCls} value={about.p2 || ""} onChange={(e) => patch("about_story", "p2", e.target.value)} data-testid="about-p2" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Story paragraph 3">
            <textarea className={taCls} value={about.p3 || ""} onChange={(e) => patch("about_story", "p3", e.target.value)} data-testid="about-p3" />
          </Field>
        </div>
        <Field label="Values section title">
          <input className={inputCls} value={about.values_title || ""} onChange={(e) => patch("about_story", "values_title", e.target.value)} placeholder="Where Tradition Meets Innovation" data-testid="about-values-title" />
        </Field>
        <Field label="Building / facility image URL">
          <input className={inputCls} value={about.building_image || ""} onChange={(e) => patch("about_story", "building_image", e.target.value)} placeholder="https://…" data-testid="about-building-image" />
        </Field>
      </SectionCard>

      {/* Newsletter */}
      <SectionCard title="Newsletter CTA" kicker="Bottom of page" testid="editor-newsletter" onReset={() => resetSection("newsletter")}>
        <Field label="Kicker">
          <input className={inputCls} value={news.kicker || ""} onChange={(e) => patch("newsletter", "kicker", e.target.value)} placeholder="§ Stay in the loop" data-testid="news-kicker" />
        </Field>
        <Field label="Headline">
          <input className={inputCls} value={news.headline || ""} onChange={(e) => patch("newsletter", "headline", e.target.value)} placeholder="Receive our seasonal edit" data-testid="news-headline" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Body">
            <textarea className={taCls} value={news.body || ""} onChange={(e) => patch("newsletter", "body", e.target.value)} data-testid="news-body" />
          </Field>
        </div>
      </SectionCard>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 overline text-white bg-forest rounded-full px-6 py-3 disabled:opacity-40"
          data-testid="home-save-bottom"
        >
          {saving ? <Loader2 size={13} strokeWidth={1.8} className="animate-spin" /> : <Save size={13} strokeWidth={1.8} />}
          {saving ? "Saving…" : "Save homepage"}
        </button>
      </div>
    </div>
  );
};

export default HomepageEditor;
